import { Check, Maximize2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { StatusPill } from '@/components/ui/status-pill';
import { Typography } from '@/components/ui/typography';
import { cn } from '@/lib/utils';
import { isFreeTier, type FloorPlanTier } from './floorPlanOffer';
import { FLOOR_PLAN_TIER_CONTENT } from './floorPlanTierContent';
import { FloorPlanFeatureChips } from './FloorPlanFeatureChips';
import { FloorPlanPrice } from './FloorPlanPrice';
import { FloorPlanTierImage } from './FloorPlanTierImage';

interface FloorPlanTierCardProps {
  tier: FloorPlanTier;
  /** The tier the order will get. Today always Basic (see FloorPlanTierPanel). */
  isSelected: boolean;
  /** Opens the tier's example. Without it the card offers no example: no
   *  Example button and no image zoom. */
  onShowExample?: (tier: FloorPlanTier) => void;
}

/**
 * One floor plan tier. Until the paid tiers launch nothing here is a choice:
 * Basic is shown selected and cannot be unselected, and the tiers that cannot
 * be ordered yet show a disabled Coming Soon button.
 *
 * Free-ness, selection and orderability are three separate questions, so
 * making a paid tier orderable never makes it read as free or as selected.
 */
export function FloorPlanTierCard({
  tier,
  isSelected,
  onShowExample
}: FloorPlanTierCardProps) {
  const content = FLOOR_PLAN_TIER_CONTENT[tier.id];
  const image = (
    <FloorPlanTierImage
      tierId={tier.id}
      onlineImage={tier.onlineImage}
      className={cn(
        'h-full w-full object-contain',
        !tier.orderable && 'opacity-80'
      )}
    />
  );

  return (
    <article
      aria-label={tier.name}
      className={cn(
        'flex h-full flex-col overflow-hidden rounded-2xl bg-card',
        // The selected emphasis is the border itself. An inset ring or an
        // outline drawn over the card is covered by the positioned image well
        // on the top half, so the edge read thin above and thick below.
        isSelected ? 'border-2 border-primary' : 'border border-border'
      )}
    >
      <div className="relative border-b border-border bg-background p-2.5">
        {onShowExample ? (
          // A mouse shortcut to the Example button below, which is the one
          // keyboard and screen-reader users get.
          <button
            type="button"
            tabIndex={-1}
            aria-hidden="true"
            onClick={() => onShowExample(tier)}
            className="block h-44 w-full cursor-zoom-in"
          >
            {image}
          </button>
        ) : (
          <div className="h-44 w-full">{image}</div>
        )}

        {isFreeTier(tier) && (
          <div className="absolute left-3 top-3">
            <StatusPill variant="success" icon={Check}>
              Free
            </StatusPill>
          </div>
        )}

        {onShowExample && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onShowExample(tier)}
            className="absolute bottom-3 left-3 h-8 rounded-full bg-card shadow-card"
            aria-label={`View ${tier.name} example`}
          >
            <Maximize2 className="h-3.5 w-3.5" />
            Example
          </Button>
        )}
      </div>

      <div className="flex flex-1 flex-col gap-2.5 p-4">
        <div className="flex items-baseline justify-between gap-2">
          <Typography as="h4" variant="heading-md">
            {tier.name}
          </Typography>
          <FloorPlanPrice tier={tier} />
        </div>

        <FloorPlanFeatureChips content={content} layout="stack" />

        <div className="mt-auto pt-4">
          {/* An orderable tier that is not selected gets its Select button
              when the paid tiers launch (banked follow-up); none exists yet. */}
          {isSelected ? (
            // A status, not a button: Basic cannot be unselected. The soft
            // brand fill keeps it reading as selected without looking
            // clickable like the solid primary buttons around it.
            <div className="flex h-10 w-full cursor-default items-center justify-center gap-2 rounded-lg bg-accent text-sm font-medium text-accent-foreground">
              <Check className="h-4 w-4" aria-hidden="true" />
              Selected
            </div>
          ) : (
            !tier.orderable && (
              <Button
                type="button"
                variant="outline"
                disabled
                className="w-full"
              >
                Coming Soon
              </Button>
            )
          )}
        </div>
      </div>
    </article>
  );
}
